import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Empty, Select, Space, Tabs } from 'antd';
import {
  BankOutlined,
  CalculatorOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CodeOutlined,
  CopyOutlined,
  EditOutlined,
  ExportOutlined,
  EyeOutlined,
  FileDoneOutlined,
  FileSyncOutlined,
  FilePdfOutlined,
  LinkOutlined,
  QrcodeOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { ContentBackground } from '@/components/content';
import { HowToCard } from '@/components/how-to-card';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import { PageHeader } from '@/components/page-header';
import { ROLES_PERMISSIONS } from '@/constants';
import { QueryParamsProvider } from '@/contexts/query-params/QueryParamsProvider';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { useCompaniesDropdown } from '@/hooks/queries/use-companies-dropdown';
import { API } from '@/services/api.service';
import { useAuthStore } from '@/store/useAuthStore';
import AbJournalModal from './_components/AbJournalModal';
import AccountsPanel from './_components/AccountsPanel';
import CalculationsPanel from './_components/CalculationsPanel';
import PeoplePanel from './_components/PeoplePanel';
import NotificationsPanel from './_components/NotificationsPanel';
import DriveStatusLine from './_components/DriveStatusLine';
import ImportModal from './_components/ImportModal';
import StatementXmlModal from './_components/StatementXmlModal';
import PaymentSlipModal from './_components/PaymentSlipModal';
import ReturnsPanel from './_components/ReturnsPanel';
import SalaryPaymentOrdersModal from '@/components/salary-payment/SalaryPaymentOrdersModal';
import ReturnDrawer from './_components/ReturnDrawer';
import { errorText } from './_components/format';
import { ALL_CLEARED, TAX_RETURNS_DEFAULT_PARAMS } from './_components/listParams';

/**
 * Tax returns and whether they are paid, per project, from the imported bank
 * statements; the rent and other income calculated on the platform; the
 * landlords and properties; the accounts statements are read from. Company,
 * tab, page and filters live in the address, so a link or a reload opens the
 * same list, and every list pages and filters on the server.
 *
 * @returns {JSX.Element}
 */
function TaxReturnsContent() {
  const { t } = useTranslation(['taxReturns', 'menus', 'forms']);
  const queryClient = useQueryClient();
  const userRoles = useAuthStore((state) => state.user?.roles) || [];
  const canManage = ROLES_PERMISSIONS.TAX_RETURNS.MANAGE.requiredRoles.some((r) => userRoles.includes(r));

  const { queryParams, setQueryParams } = useQueryParamsContext();
  const { company } = queryParams;
  const tab = queryParams.tab || 'returns';
  // Another company or tab starts from its first page with no filters.
  const setCompany = (value) => setQueryParams({ ...ALL_CLEARED, company: value || undefined, tab: 'returns', offset: 1 });
  const setTab = (value, extra = {}) => setQueryParams({ ...ALL_CLEARED, tab: value, offset: 1, ...extra });

  const [importOpen, setImportOpen] = useState(false);
  const [statementXmlOpen, setStatementXmlOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  // PDPO returns picked for a journal file for AB Soft.
  const [selected, setSelected] = useState([]);
  const [abOpen, setAbOpen] = useState(false);
  const [slipOf, setSlipOf] = useState(null);
  const [salaryOrdersOpen, setSalaryOrdersOpen] = useState(false);
  // Salary orders exist only for a company whose payroll AB delivers with its PPP-PD filings.
  const { data: salaryPayouts } = useQuery({
    queryKey: ['salary-payment-payouts', company],
    queryFn: () => API.exports.checkSalaryPayment({ companyId: company }),
    enabled: Boolean(company),
    // Refused unless the caller sees the company's salaries; no button then.
    retry: false,
  });

  const { data: companies } = useCompaniesDropdown({ queryParams: {} });
  useEffect(() => {
    if (!company && companies?.options?.length === 1) setCompany(companies.options[0].value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, company]);

  // What the whole page needs whatever the tab: the projects, whether statements
  // are imported and whether there is any return at all.
  const { data: meta, error } = useQuery({
    queryKey: ['tax-returns', company, 'meta'],
    queryFn: () => API.taxReturns.getTaxReturns({ company, limit: 1 }),
    enabled: Boolean(company),
  });
  const projects = meta?.projects || [];
  // Paid or not can only be told from bank statements. A company we hold none
  // for sees what to pay and a payment slip instead of a status that would
  // always read „no statement".
  const hasStatements = (meta?.statementCoverage?.length || 0) > 0;
  // Nothing imported for the company yet: say so and what to do, instead of an empty list.
  const noReturns = Boolean(meta) && !meta.count;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['tax-returns', company] });

  // Steps of the "How to use" box, with the icons in the order of the translated steps.
  const guideSteps = (key, icons, actions = []) => {
    // A key returned as text means the translations are not loaded yet.
    const steps = t(`howTo.${key}.steps`, { returnObjects: true });
    return (Array.isArray(steps) ? steps : []).map((step, i) => ({
      ...step,
      icon: icons[i],
      onClick: actions[i] || undefined,
    }));
  };
  const openImport = () => setImportOpen(true);
  // Overdue first: the rows the accountant has to chase.
  const showOverdue = () => setTab('returns', { status: 'unpaid' });
  const showDueSoon = () => setTab('returns', { dueSoon: true });
  const guide = canManage
    ? [
        {
          title: t('howTo.pppd.title'),
          steps: guideSteps(
            'pppd',
            [FilePdfOutlined, BankOutlined, LinkOutlined, WarningOutlined, ClockCircleOutlined, EditOutlined],
            [openImport, openImport, () => setTab('accounts'), showOverdue, showDueSoon]
          ),
        },
        {
          title: t('howTo.pdpo.title'),
          steps: guideSteps('pdpo', [CodeOutlined, FilePdfOutlined, UploadOutlined, ExportOutlined], [null, null, openImport]),
        },
        {
          title: t('howTo.calc.title'),
          steps: guideSteps('calc', [CalculatorOutlined, CopyOutlined, FileDoneOutlined, QrcodeOutlined], [
            () => setTab('calculations'),
          ]),
        },
      ]
    : [
        {
          title: t('howTo.client.title'),
          // The status step only means something where statements are imported.
          steps: guideSteps('client', [EyeOutlined, ClockCircleOutlined, QrcodeOutlined, CheckCircleOutlined]).slice(
            0,
            hasStatements ? 4 : 3
          ),
        },
      ];

  const tabs = [
    { key: 'returns', label: t('tabs.returns') },
    { key: 'calculations', label: t('tabs.calculations') },
    // People and properties hold ID numbers: HLB's own list, never shown to the client.
    canManage && { key: 'people', label: t('tabs.people') },
    // Statements are HLB's to import; a client without any has nothing to see there.
    (canManage || hasStatements) && { key: 'accounts', label: t('tabs.accounts') },
    // Mails to the client's people about returns to pay: set up and checked by HLB.
    canManage && { key: 'notifications', label: t('tabs.notifications') },
  ].filter(Boolean);
  const activeTab = tabs.some((x) => x.key === tab) ? tab : 'returns';

  // What the header bar holds. With one company and nothing to import there is
  // nothing in it, and the bar must go with it: `page__header` keeps its
  // `min-h-12` even when empty, which left a blank band above the page.
  const showCompanySelect = !companies?.isSingle;
  const showImportActions = canManage && Boolean(company);
  const showSalaryOrders = Boolean(company) && salaryPayouts?.payouts?.length > 0;
  const hasHeader = showCompanySelect || showImportActions || showSalaryOrders;

  const panels = {
    returns: noReturns ? (
      <Empty
        style={{ margin: '48px 0' }}
        description={
          <Space direction="vertical" size={4}>
            <span>{t('empty.title')}</span>
            <span className="text-sm opacity-70">{t(canManage ? 'empty.manage' : 'empty.client')}</span>
          </Space>
        }
      >
        {canManage && (
          <Button shape="round" type="primary" icon={<UploadOutlined />} onClick={openImport}>
            {t('import.button')}
          </Button>
        )}
      </Empty>
    ) : (
      <ReturnsPanel
        company={company}
        canManage={canManage}
        hasStatements={hasStatements}
        projects={projects}
        selected={selected}
        onSelect={setSelected}
        onAb={() => setAbOpen(true)}
        onOpen={setOpenId}
        onSlip={setSlipOf}
      />
    ),
    calculations: <CalculationsPanel company={company} projects={projects} canManage={canManage} onOpenReturn={setOpenId} />,
    people: <PeoplePanel company={company} projects={projects} />,
    accounts: <AccountsPanel company={company} projects={projects} canManage={canManage} />,
    notifications: <NotificationsPanel company={company} />,
  };

  return (
    <RootLayout metadata={{ title: t('taxReturns', { ns: 'menus' }) }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />
      <style>
        {[
          '.tax-return-overdue td { background: rgba(255, 77, 79, 0.12) !important; color: #cf1322; font-weight: 500; }',
          '.tax-return-due-soon td { background: rgba(250, 140, 22, 0.10) !important; }',
          // A slow pulse on the reminder, so an approaching due date catches the eye without shouting.
          '@keyframes tax-return-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }',
          '.tax-return-due-tag, .tax-return-due-pulse { animation: tax-return-pulse 1.6s ease-in-out infinite; }',
          '@media (prefers-reduced-motion: reduce) { .tax-return-due-tag, .tax-return-due-pulse { animation: none; } }',
        ].join('\n')}
      </style>

      <Section>
        <ContainerFluid className="foreground-wrapper">
          {hasHeader && (
            <PageHeader>
              <div className="flex w-full flex-wrap items-center gap-2 bg-white p-1">
                {showCompanySelect && (
                  <Select
                    showSearch
                    optionFilterProp="label"
                    style={{ width: 460 }}
                    popupMatchSelectWidth={false}
                    placeholder={t('filters.company')}
                    value={company}
                    onChange={(v) => {
                      setCompany(v);
                      setSelected([]);
                    }}
                    options={companies?.options || []}
                  />
                )}
                {showImportActions && (
                  <Button shape="round" type="primary" icon={<UploadOutlined />} onClick={openImport}>
                    {t('import.button')}
                  </Button>
                )}
                {showImportActions && (
                  <Button shape="round" icon={<FileSyncOutlined />} onClick={() => setStatementXmlOpen(true)}>
                    {t('statementXml.button')}
                  </Button>
                )}
                {showSalaryOrders && (
                  <Button shape="round" icon={<BankOutlined />} onClick={() => setSalaryOrdersOpen(true)}>
                    {t('salaryOrdersTitle', { ns: 'forms' })}
                  </Button>
                )}
              </div>
            </PageHeader>
          )}

          <div className={clsx('rounded-lg bg-white p-4', hasHeader && 'mt-3')}>
            <p className="mb-3 text-sm opacity-70">{t(company && meta && !hasStatements ? 'subtitleNoStatements' : 'subtitle')}</p>
            {company && (
              <HowToCard
                title={t('howTo.title')}
                sections={guide}
                note={canManage ? t('howTo.note') : undefined}
                open={meta ? noReturns : undefined}
              />
            )}
            {canManage && company && <DriveStatusLine company={company} onImported={refresh} />}
            {!company && <Alert type="info" showIcon message={t('pickCompany')} />}
            {error && <Alert type="error" showIcon message={errorText(t, error)} />}

            {company && (
              <>
                <Tabs activeKey={activeTab} onChange={(key) => setTab(key)} items={tabs} />
                {panels[activeTab]}
              </>
            )}
          </div>
        </ContainerFluid>
      </Section>

      <ImportModal open={importOpen} company={company} projects={projects} onClose={() => setImportOpen(false)} onImported={refresh} />
      <StatementXmlModal open={statementXmlOpen} company={company} onClose={() => setStatementXmlOpen(false)} />
      <PaymentSlipModal
        slip={slipOf?.paymentSlip}
        title={
          slipOf
            ? `${slipOf.slipKind === 'interest' ? t('interest.slipTitle') : t('slip.title')}: ${slipOf.title || slipOf.bop}`
            : ''
        }
        note={slipOf?.slipKind === 'interest' ? t('interest.slipNote') : undefined}
        onClose={() => setSlipOf(null)}
      />
      <SalaryPaymentOrdersModal open={salaryOrdersOpen} companyId={company} onClose={() => setSalaryOrdersOpen(false)} />
      <AbJournalModal
        open={abOpen}
        company={company}
        returns={selected}
        onClose={() => setAbOpen(false)}
        onExported={() => {
          setSelected([]);
          refresh();
        }}
      />
      <ReturnDrawer
        company={company}
        hasStatements={hasStatements}
        onSlip={setSlipOf}
        id={openId}
        projects={projects}
        canManage={canManage}
        onClose={() => setOpenId(null)}
        onChanged={refresh}
      />
    </RootLayout>
  );
}

function TaxReturnsWithParams() {
  return (
    <QueryParamsProvider defaultParams={TAX_RETURNS_DEFAULT_PARAMS}>
      <TaxReturnsContent />
    </QueryParamsProvider>
  );
}

const TaxReturnsPage = withPrivateRoute(TaxReturnsWithParams, {
  ...ROLES_PERMISSIONS.TAX_RETURNS.VIEW,
  requiredCompanyFlag: 'hasTaxReturns',
});
TaxReturnsPage.displayName = 'TaxReturnsPage';

export default TaxReturnsPage;
